import React,{useState} from 'react'
import {motion} from 'framer-motion'
import { supabase } from '../supabaseClient'

const Hero = () => {
  const [email, setEmail] = useState('')
  const [showShareTooltip, setShowShareTooltip] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      const { data, error } = await supabase
        .from('landing_page_users')
        .insert([{ email: email }])

      if (error) {
        setMessage('Failed!')
      } else {
        setMessage('Success!')
        setEmail('')
      }
    } catch (err) {
      setMessage('Failed!')
    } finally {
      setLoading(false)
    }
  }

  const shareData = {
    title: 'WWSC Free Course: 10 Weeks to Stronger Street Outreach',
    text: 'Join me in this free 10-week email course for frontline workers working with street children. 3 practical lessons per week - field-ready, no fluff!',
    url: window.location.href
  }

  const handleShare = async (platform) => {
    const encodedTitle = encodeURIComponent(shareData.title)
    const encodedText = encodeURIComponent(shareData.text)
    const encodedUrl = encodeURIComponent(shareData.url)

    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank')
        break
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, '_blank')
        break
      case 'email':
        window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedText}%20${encodedUrl}`
        break
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share(shareData)
          } catch (err) {
            console.log('Error sharing:', err)
          }
        }
        break
    }
  }

  return (
    <section className="relative h-full flex items-center justify-center overflow-hidden pt-20" style={{backgroundColor: '#A96D1A'}}>
      {/* Subtle ripple effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full animate-pulse" style={{backgroundColor: 'rgba(255,177,144,0.1)'}} ></div>
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full animate-pulse" style={{backgroundColor: 'rgba(255,177,144,0.05)'}} ></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          {/* Left Content - More compact */}
          <motion.div 
            initial={{opacity: 0, x: -50}} 
            animate={{opacity: 1, x: 0}} 
            transition={{duration: 0.8, ease: "easeOut"}} 
            className="lg:col-span-2 text-left"
          >
            <motion.h1 
              initial={{opacity: 0, y: 30}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.2, duration: 0.8, ease: "easeOut"}} 
              className="text-4xl lg:text-5xl font-bold text-white mb-4 font-poppins leading-tight"
            >
              10 Weeks to{' '}
              <span style={{color: '#FFB190'}}>Stronger Street Outreach</span>
            </motion.h1>
            
            <motion.h2 
              initial={{opacity: 0, y: 20}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.3, duration: 0.8, ease: "easeOut"}} 
              className="text-2xl lg:text-3xl font-semibold text-white/90 mb-4 font-poppins leading-tight"
            >
              3 short Email Lessons a Week.{' '}
              <span style={{color: '#FFB190'}}>Field-ready. No Fluff.</span>
            </motion.h2>
            
            <motion.p 
              initial={{opacity: 0, y: 20}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.4, duration: 0.8, ease: "easeOut"}} 
              className="text-white/90 mb-6 leading-relaxed font-lato max-w-lg" 
              style={{fontSize: '1.2rem'}}
            >
              Working with street children can be tough - but you don't have to figure it out alone. Get 30 bite-size lessons (3-5 minutes each) straight to your inbox. Practical steps from first contact to safe rescue.
            </motion.p>
            
            <motion.div 
              initial={{opacity: 0, y: 20}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.5, duration: 0.8, ease: "easeOut"}} 
              className="mb-6"
            >
              <p className="text-white/90 font-semibold text-lg font-poppins mb-3">
                Start Date: <span style={{color: '#FFB190'}}>Monday 10 November 2025</span>
              </p>
            </motion.div>
            
            <motion.form 
              initial={{opacity: 0, y: 20}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.6, duration: 0.8, ease: "easeOut"}} 
              onSubmit={handleSubmit} 
              className="flex flex-col sm:flex-row gap-3 max-w-md mb-3"
            >
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="Email address" 
                className="flex-1 px-5 py-3 rounded-md border border-white/30 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 text-base" 
                style={{backgroundColor: '#FFB190'}} 
                required 
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 text-base whitespace-nowrap rounded-md font-semibold transition-all duration-300 hover:transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{backgroundColor: '#82A6C3', color: 'white'}}
              >
                {loading ? 'Joining...' : 'Join Free Course'}
              </button>
            </motion.form>

            {message && (
              <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                className={`mb-3 p-3 rounded-md ${
                  message === 'Success!'
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-red-100 text-red-800 border border-red-300'
                }`}
              >
                {message}
              </motion.div>
            )}

            <motion.p
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.7, duration: 0.8, ease: "easeOut"}}
              className="text-white/70 text-sm font-lato"
            >
              No spam. Unsubscribe anytime.
            </motion.p>
          </motion.div>
          
          {/* Right Image - 15% bigger */}
          <motion.div 
            initial={{opacity: 0, x: 50}} 
            animate={{opacity: 1, x: 0}} 
            transition={{delay: 0.3, duration: 0.8, ease: "easeOut"}} 
            className="flex justify-center lg:justify-end"
          >
            <img 
              src="https://images.groovetech.io/AIeg2_WjoOO4YMCw_2ySgISCh9XTlYKmHluTMnOgGBI/rs:fit:0:0:0/g:no:0:0/c:0:0/aHR0cHM6Ly9hc3NldHMuZ3Jvb3ZlYXBwcy5jb20vaW1hZ2VzL2MxZTdhMGZlLTQ0NGItNDEzNy04NDNiLTg5ZmRhZmUwYjVkMS8xNzI4OTgxNzczX0hFUk9MZXNzU0NBUlMuSlBH.webp" 
              alt="Child Portrait" 
              className="w-full max-w-xs rounded-lg shadow-xl" 
              style={{maxWidth: '345px'}} 
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Hero